import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import foxHead from '@/assets/fox-head.png';
import viralujStamp from '@/assets/viraluj-stamp.png';

interface WelcomeDialogProps {
  userName?: string;
}

const ONBOARDING_COMPLETE_KEY = 'onboarding_completed';

// Scope the "already seen" marker by userId so a previous account on the
// same browser doesn't prevent a new account from seeing the welcome
// letter. The legacy unscoped key (`welcome_dialog_shown`) is intentionally
// ignored — its presence in localStorage on a shared browser was the
// reported bug — so a small number of pre-fix users will see the letter
// once more on next login.
const welcomeShownKeyFor = (userId: string | null | undefined) =>
  userId ? `welcome_dialog_shown:${userId}` : null;

// Module-level flag so the stamp is only preloaded once across mounts
let stampPreloaded = false;

// Eagerly preload stamp image at module load time
const stampImg = new Image();
stampImg.onload = () => { stampPreloaded = true; };
stampImg.onerror = () => { stampPreloaded = true; };
stampImg.src = viralujStamp;

export const WelcomeDialog = ({ userName }: WelcomeDialogProps) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [assetsReady, setAssetsReady] = useState(false);
  const [instagramUsername, setInstagramUsername] = useState<string>('');

  // Preload stamp image and open dialog only when it's actually loaded
  useEffect(() => {
    let isMounted = true;

    const preloadStamp = async () => {
      if (stampPreloaded) {
        if (isMounted) setAssetsReady(true);
        return;
      }

      const img = new Image();
      img.src = viralujStamp;

      if (img.complete) {
        stampPreloaded = true;
        if (isMounted) setAssetsReady(true);
        return;
      }

      try {
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('stamp-load-error'));
        });

        if (typeof img.decode === 'function') {
          try {
            await img.decode();
          } catch {
            // decode failures should not block dialog after successful load
          }
        }
      } catch {
        // If preload fails, do not block the welcome dialog forever
      }

      stampPreloaded = true;
      if (isMounted) setAssetsReady(true);
    };

    preloadStamp();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchInstagramUsername = async () => {
      if (!user?.id) return;

      const { data } = await supabase
        .from('user_profiles')
        .select('instagram_username')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data?.instagram_username) {
        setInstagramUsername(data.instagram_username);
      }
    };

    fetchInstagramUsername();
  }, [user?.id]);

  // Track whether welcome should be shown (separate from actually opening)
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const checkShouldShowWelcome = (): boolean => {
      const forceShow = localStorage.getItem('welcome_letter_force');
      if (forceShow === 'true') {
        localStorage.removeItem('welcome_letter_force');
        setShouldShow(true);
        return true;
      }

      if (!user) return false;
      const welcomeKey = welcomeShownKeyFor(user.id);
      if (welcomeKey && localStorage.getItem(welcomeKey)) return false;

      const onboardingComplete = localStorage.getItem(ONBOARDING_COMPLETE_KEY);
      if (onboardingComplete === 'true') {
        setShouldShow(true);
        return true;
      }
      return false;
    };

    if (checkShouldShowWelcome()) return;

    const interval = setInterval(() => {
      if (checkShouldShowWelcome()) {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [user]);

  // Only open dialog when both conditions are met: should show AND assets ready
  useEffect(() => {
    if (shouldShow && assetsReady && !isOpen) {
      setIsOpen(true);
    }
  }, [shouldShow, assetsReady]);

  const markSeen = () => {
    const welcomeKey = welcomeShownKeyFor(user?.id);
    if (welcomeKey) localStorage.setItem(welcomeKey, 'true');
  };

  const handleClose = () => {
    setIsOpen(false);
    markSeen();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) markSeen();
    setIsOpen(open);
  };

  const displayName = instagramUsername || userName || '';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] p-0 border-none shadow-2xl rounded-2xl overflow-hidden">
        <ScrollArea className="max-h-[90vh]">
          <div className="bg-background px-8 pt-8 pb-6 relative">
            {/* Headline */}
            <h2
              className="text-center text-foreground font-semibold leading-snug text-[22px]"
            >
              Cześć, {displayName}.
              <br />
              Dobrze, że tu jesteś.
            </h2>

            {/* Divider */}
            <div className="h-px bg-border/60 mt-5 mb-5" />

            {/* Letter body */}
            <p
              className="text-muted-foreground mt-4 text-center leading-relaxed text-[15px]"
            >
              Naprawdę się cieszymy że zdecydowałeś się na ten krok.
              <br />
              Włożyliśmy w Viraluj dużo serca i robimy to nadal każdego dnia.
              <br />
              Mamy nadzieję że poczujesz to od pierwszych chwil.
              <br /><br />
              Życzymy Ci dużo radości podczas tworzenia, lekkości w każdym
              materiale i samych dobrych momentów po drodze.
            </p>

            {/* Signature */}
            <p
              className="text-muted-foreground/60 mt-4 text-center italic text-[13px]"
            >
              Wojtek i Łukasz, ekipa Viraluj
            </p>

            {/* Stamp */}
            <div className="flex justify-end mt-2 mr-2">
              <img
                src={viralujStamp}
                alt="Viraluj stamp"
                loading="eager"
                decoding="sync"
                fetchPriority="high"
                className="w-20 h-20"
                style={{ transform: 'rotate(-8deg)' }}
              />
            </div>

            {/* Divider */}
            <div className="h-px bg-border/60 mt-2 mb-5" />

            {/* CTA */}
            <Button
              onClick={handleClose}
              className="w-full group"
              size="lg"
            >
              Zaczynamy
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
